import type { StatsOverview } from './admin-api';
import { esc, statCard, timeAgo, formatMoney } from './admin-ui';

/** Core sends revenue in cents; prefer the explicit *Cents fields over the deprecated aliases (2026-10-19). */
const revenueCents = (d: StatsOverview, key: 'total' | 'today'): number =>
  (key === 'total' ? d.revenue.totalCents : d.revenue.todayCents) ?? d.revenue[key];

export type WidgetId =
  | 'total-players' | 'new-today' | 'new-week' | 'online'
  | 'payments-total' | 'payments-today'
  | 'pending-actions' | 'recent-players';

export type WidgetSize = 'sm' | 'md' | 'lg';

export interface WidgetDef {
  id: WidgetId;
  title: string;
  defaultSize: WidgetSize;
  render: (data: StatsOverview) => string;
}

const SIZE_CLASS: Record<WidgetSize, string> = {
  sm: 'col-span-1',
  md: 'col-span-1 sm:col-span-2',
  lg: 'col-span-1 sm:col-span-2 md:col-span-3',
};

export const WIDGET_REGISTRY: WidgetDef[] = [
  { id: 'total-players', title: 'Total Players', defaultSize: 'sm',
    render: d => statCard('Total Players', d.players.total) },
  { id: 'new-today', title: 'New Today', defaultSize: 'sm',
    render: d => statCard('New Today', d.players.newToday) },
  { id: 'new-week', title: 'New This Week', defaultSize: 'sm',
    render: d => statCard('New This Week', d.players.newThisWeek) },
  { id: 'online', title: 'Online', defaultSize: 'sm',
    render: d => statCard('Online', d.players.online) },
  { id: 'payments-total', title: 'Payments Total', defaultSize: 'sm',
    render: d => statCard('Payments Total', formatMoney(revenueCents(d, 'total'), d.revenue.currency ?? 'EUR')) },
  { id: 'payments-today', title: 'Payments Today', defaultSize: 'sm',
    render: d => statCard('Payments Today', formatMoney(revenueCents(d, 'today'), d.revenue.currency ?? 'EUR')) },
  { id: 'pending-actions', title: 'Pending Actions', defaultSize: 'md',
    render: d => `
      <div class="frame-pewter p-4 h-full">
        <h3 class="font-heading text-gold text-sm mb-3">Pending Actions</h3>
        <div class="flex items-center justify-between min-h-11 px-3 text-[13px] text-text">
          <span>KYC Reviews</span><span class="text-gold font-semibold">${d.pending.kyc}</span>
        </div>
        <div class="flex items-center justify-between min-h-11 px-3 text-[13px] text-text">
          <span>Pending Withdrawals</span><span class="text-gold font-semibold">${d.pending.withdrawals}</span>
        </div>
      </div>` },
  { id: 'recent-players', title: 'Recent Players', defaultSize: 'md',
    render: d => `
      <div class="frame-pewter p-4 h-full">
        <h3 class="font-heading text-gold text-sm mb-3">Recent Players</h3>
        ${d.recentPlayers.length === 0
          ? '<p class="text-muted text-[13px]">No recent players.</p>'
          : d.recentPlayers.map(p => `
            <div class="flex items-center gap-2 py-1.5 text-[13px]">
              <span class="w-7 h-7 rounded-full bg-gold/20 text-gold flex items-center justify-center text-xs font-bold shrink-0">
                ${esc((p.username ?? p.email)[0].toUpperCase())}
              </span>
              <span class="text-text truncate">${esc(p.username ?? p.email)}</span>
              <span class="text-muted ml-auto shrink-0">${timeAgo(p.createdAt)}</span>
            </div>
          `).join('')}
      </div>` },
];

export function widgetById(id: WidgetId): WidgetDef | undefined {
  return WIDGET_REGISTRY.find(w => w.id === id);
}

export function renderWidget(
  widget: WidgetDef,
  data: StatsOverview,
  editMode: boolean,
): string {
  return `
    <div class="admin-widget ${SIZE_CLASS[widget.defaultSize]} relative group"
         data-widget-id="${widget.id}" ${editMode ? 'draggable="true"' : ''}>
      ${editMode ? `
        <div class="absolute top-1 right-1 z-10 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button class="widget-drag-handle w-8 h-8 flex items-center justify-center bg-surface-2 rounded text-muted hover:text-text cursor-grab text-xs"
            title="Drag to reorder">&#9783;</button>
          <button class="widget-hide w-8 h-8 flex items-center justify-center bg-surface-2 rounded text-muted hover:text-red-400 text-xs"
            data-hide-widget="${widget.id}" title="Hide widget">&times;</button>
        </div>
      ` : ''}
      ${widget.render(data)}
    </div>`;
}
